/**
 * Client error reporting — docs/spec/06 §6, 09 §6.
 *
 * Two rules do the work here:
 *
 *   1. Report only what is UNEXPECTED. Render crashes and 5xx are incidents;
 *      validation, auth and business-rule 4xx are user-facing outcomes. Sending
 *      the latter would bury the one real incident under a week of noise, and
 *      the noisiest signal is the one people learn to ignore.
 *   2. Redact before sending. See src/lib/redact.ts.
 *
 * The transport is a seam: the real Sentry-compatible DSN is Q11 and env-gated
 * (real in staging/prod, absent locally), so with no sink registered this is a
 * no-op rather than a crash.
 */
import { mapError } from '../api/errorMessages'
import { redact } from './redact'

export interface ReportableEvent {
  readonly kind: 'crash' | 'server-error'
  readonly message: string
  readonly requestId: string | null
  /** Already redacted by the time a sink sees it. */
  readonly context: Record<string, unknown>
}

export type ReportSink = (event: ReportableEvent) => void

let sink: ReportSink | null = null

/** Wire the real reporter (staging/prod). Passing null disables reporting. */
export function registerReportSink(next: ReportSink | null): void {
  sink = next
}

/**
 * A 500 that TanStack Query retries twice is still ONE incident.
 *
 * 08 §2.5 requires a simulated 500 to report exactly once; without this, the
 * retry policy would triple every server error and make the volume meaningless.
 */
const DEDUPE_WINDOW_MS = 10_000
const recent = new Map<string, number>()

function shouldSend(signature: string, now: number): boolean {
  for (const [key, at] of recent) {
    if (now - at > DEDUPE_WINDOW_MS) recent.delete(key)
  }
  if (recent.has(signature)) return false
  recent.set(signature, now)
  return true
}

function emit(event: ReportableEvent, now: number): void {
  if (sink === null) return
  if (!shouldSend(`${event.kind}:${event.message}:${event.requestId ?? ''}`, now)) return
  sink(event)
}

/** Report a render crash caught by an ErrorBoundary (01 §7). */
export function reportCrash(
  error: Error,
  context: Record<string, unknown> = {},
  now: number = Date.now(),
): void {
  emit(
    {
      kind: 'crash',
      message: error.message,
      requestId: null,
      context: redact({ ...context, error }) as Record<string, unknown>,
    },
    now,
  )
}

/**
 * Report a request failure, but only if it is genuinely unexpected.
 *
 * Returns whether it was reported, so callers can assert the negative — "a
 * validation error did NOT page anyone" is exactly the kind of claim that needs
 * instrumentation rather than trust.
 */
export function reportIfUnexpected(
  error: unknown,
  context: Record<string, unknown> = {},
  now: number = Date.now(),
): boolean {
  const mapped = mapError(error)
  if (!mapped.report) return false

  emit(
    {
      kind: 'server-error',
      message: mapped.message,
      requestId: mapped.requestId,
      context: redact({ ...context, kind: mapped.kind }) as Record<string, unknown>,
    },
    now,
  )
  return true
}

/** Test-only: clear the sink and the dedupe window. */
export function resetReporterForTests(): void {
  sink = null
  recent.clear()
}
